import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { accountModel } from '@/common/models/account'
import { applicationModel } from '@/common/models/application'
import { applicationAccountUserModel } from '@/common/models/applicationAccountUser'
import { applicationAccountUsersAuthModel } from '@/common/models/applicationAccountUsersAuth'
import { personModel } from '@/common/models/person'
import { userModel } from '@/common/models/user'
import { userPreferenceModel } from '@/common/models/userPreference'
import { api, ApiError } from '@/lib/axios'

export const currentUserAccountsDTO = userModel.extend({
  Person: personModel,
  UserPreference: userPreferenceModel,
  ApplicationAccountUsers: z.array(
    applicationAccountUserModel.extend({
      Account: accountModel,
      Application: applicationModel,
      ApplicationAccountUsersAuth: z.array(applicationAccountUsersAuthModel),
    }),
  ),
})

export type CurrentUserAccounts = z.infer<typeof currentUserAccountsDTO>

async function getCurrentUserAccounts(userId: string) {
  const { data } = await api.backend.get<CurrentUserAccounts[]>(
    '/user/findAll',
    {
      params: {
        relations: {
          where: {
            id: userId,
            isActive: true,
          },
          include: {
            Person: true,
            UserPreference: true,
            ApplicationAccountUsers: {
              where: { isActive: true },
              include: {
                Account: true,
                Application: true,
                ApplicationAccountUsersAuth: {
                  where: { isActive: true },
                },
              },
            },
          },
        },
      },
    },
  )

  return data[0]
}

export function useGetCurrentUserAccounts() {
  const { userInfo } = useGetUserInfo()

  const { data, isLoading, error } = useQuery<CurrentUserAccounts, ApiError>({
    queryKey: ['/user/current-accounts', userInfo?.id],
    queryFn: () => getCurrentUserAccounts(userInfo?.id || ''),
    enabled: !!userInfo?.id,
  })

  return {
    currentUserAccounts: data,
    isLoadingCurrentUserAccounts: isLoading,
    currentUserAccountsError: error,
  }
}
